import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMetaSpec,
  nsFileToI18n,
  PHASE1_SEO_CONTENT_PAGES,
  resolvePageNameFromPath,
  toI18nKey,
} from '../../lib/seoRouteMeta';
import { RestoSeoPhotos } from '../hospitality/RestoSeoPhotos';

/**
 * Visible bottom-of-page SEO body (synced with prerender #seo-content).
 * Prerender #seo-content is hidden after JS via .js-ready — this keeps
 * equivalent copy available for users and JS-capable crawlers.
 */
export const SeoVerticalBody: React.FC = () => {
  const { t } = useTranslation(['seo', 'pricing', 'resto', 'office', 'home', 'music', 'team']);

  if (typeof window === 'undefined') return null;

  const pageName = resolvePageNameFromPath(window.location.pathname);
  // Music keeps crawlable #seo-content in prerender only — no visible duplicate body.
  if (!PHASE1_SEO_CONTENT_PAGES.has(pageName) || pageName === 'music') return null;

  const spec = getMetaSpec(pageName);
  if (!spec) return null;

  const nsFile = spec.skinNs || spec.ns;
  const ns = nsFileToI18n(nsFile);
  if (!ns || !spec.bodyKey) return null;

  const bodyKey = toI18nKey(spec.bodyKey);
  const h1Key = toI18nKey(spec.h1Key);
  const body = bodyKey ? t(bodyKey, { ns, defaultValue: '' }) : '';
  const h1 = h1Key ? t(h1Key, { ns, defaultValue: '' }) : '';

  if (!body || body === bodyKey || body.length < 40) return null;

  const paragraphs = body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const cream = pageName === 'resto';

  return (
    <section
      className={cream ? 'seo-vertical-body bg-komby-cream text-gray-700' : 'seo-vertical-body text-gray-700 dark:text-gray-300'}
      aria-label="SEO"
      data-seo-page={pageName}
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 ${cream ? 'grid items-center gap-10 lg:grid-cols-12' : ''}`}>
        <div className={cream ? 'lg:col-span-6' : undefined}>
          {h1 && h1 !== h1Key ? (
            <h2 className={`text-2xl font-semibold mb-4 ${cream ? 'font-display text-gray-950' : 'text-gray-900 dark:text-white'}`}>
              {h1}
            </h2>
          ) : null}
          <div className="space-y-4 text-base leading-relaxed text-justify">
            {paragraphs.map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
          </div>
        </div>
        {cream ? <RestoSeoPhotos className="lg:col-span-6" /> : null}
      </div>
    </section>
  );
};
